import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'
import { useChartAxisApi } from '@aytm/app/stats/page/question-views/components/charts/highcharts/hooks/useChartAxisApi'
import { btnsTitle } from '@aytm/app/stats/page/question-views/ra/lib/utils'

import { calcDefaultChartMode } from '@aytm/app/stats/page/question-views/ra/lib/report-params'

import { getChartModeTypes } from '@aytm/app/stats/page/question-views/ra/components/chart/lib/chartModes'
import { buildStackedHcAnswers } from '@aytm/app/stats/page/question-views/ra/components/chart/lib/hcAnswers'

const RapidAssociationChart = ({
  questionId,
  questionKey,
  qType,
  btnsType,
  viewMode,
  chartMode: chartModeProp,
  questionChartMode,
  globalChartMode,
  onChartModeChange,
  zoomLevel,
  globalZoomLevel,
  onZoomChange,
  answer,
  answerVariants,
  sortOrder,
  rType,
  format,
  isTraitType,
  colorTheme,
  chartLegendMode,
  confidenceLevel,
  significanceLevel,
  confidenceLevels,
  features,
  onChartReady
}) => {
  const chartRef = useChartAxisApi(onChartReady)

  const percentsOnly = format?.percentsOnly === true
  const ignoreTraits = format?.ignoreTraits === true || !isTraitType
  const ignoreAmount = percentsOnly || format?.ignoreAmount === true

  const chartModeTypes = useMemo(() => getChartModeTypes(viewMode, percentsOnly), [viewMode, percentsOnly])

  const chartMode = useMemo(
    () =>
      calcDefaultChartMode({
        chartMode: chartModeProp,
        questionChartMode,
        globalChartMode,
        viewMode,
        percentsOnly
      }),
    [chartModeProp, questionChartMode, globalChartMode, viewMode, percentsOnly]
  )

  const chartProps = useMemo(() => {
    if (viewMode === 'bar') {
      return {
        chartMode,
        answerVariants,
        answer,
        qType,
        sortOrder,
        viewMode,
        rType,
        zoomType: true,
        ignoreTraits,
        ignoreAmount,
        // the margin of error is drawn on the bar view only, and the best-fit axis leaves room for it
        confidenceLevel,
        significanceLevel,
        confidenceLevels
      }
    }

    if (viewMode === 'stacked') {
      const { batches, hcAnswers } = buildStackedHcAnswers(answerVariants, answer, btnsType)

      return {
        chartMode: `stacked_${chartMode}`,
        batches,
        answer: hcAnswers,
        qType,
        sortOrder,
        rType: 'responses',
        ignoreTraits: true
      }
    }

    const title = btnsTitle(btnsType)

    return {
      chartMode: 'scatter',
      answerVariants,
      answer,
      qType: 'respondents',
      sortOrder,
      xAxisTitle: 'Average reaction time',
      yAxisTitle: `% Indicate ${title.y}`,
      title: `Average reaction time X % ${title.y}`
    }
  }, [
    viewMode,
    chartMode,
    qType,
    btnsType,
    answer,
    answerVariants,
    sortOrder,
    rType,
    ignoreTraits,
    ignoreAmount,
    confidenceLevel,
    significanceLevel,
    confidenceLevels
  ])

  const viewOptionsDisabled = features?.view_options?.disabled

  return (
    <ChartContainer
      chartRef={chartRef}
      {...chartProps}
      questionId={questionId}
      questionKey={questionKey}
      selectorChartModeValue={chartMode}
      chartModeOverride={chartModeProp}
      globalChartMode={globalChartMode}
      chartModeTypes={chartModeTypes}
      viewOptionsDisabled={viewOptionsDisabled}
      onChartModeChange={onChartModeChange}
      zoomLevel={zoomLevel}
      globalZoomLevel={globalZoomLevel}
      onZoomChange={onZoomChange}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      features={features}
    />
  )
}

RapidAssociationChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  btnsType: PropTypes.string,
  viewMode: PropTypes.string.isRequired,
  chartMode: PropTypes.string,
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  onChartModeChange: PropTypes.func.isRequired,
  zoomLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  globalZoomLevel: PropTypes.string,
  onZoomChange: PropTypes.func,
  answer: answerPropType,
  answerVariants: PropTypes.array,
  sortOrder: PropTypes.string,
  rType: PropTypes.string,
  format: PropTypes.object,
  isTraitType: PropTypes.bool,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  confidenceLevels: PropTypes.array,
  features: PropTypes.object,
  onChartReady: PropTypes.func
}

RapidAssociationChart.displayName = 'RapidAssociationChart'

export default RapidAssociationChart
